import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { trackTitle, venueLabel } from "@/features/racing/venue";

import { RaceDetails } from "./RaceDetails";
import { localDateTime, localDay, localTime } from "./scheduleState";

export type ScheduleEntry = {
  raceId: string;
  track: string;
  trackName?: string;
  distanceM: number;
  at: string | null;
  status: string;
  racecardHash?: string;
  order?: number[];
  raceNumber?: string;
  fieldSize?: number;
  stage?: string;
  weather?: string;
  stageName?: string;
  weatherName?: string;
};

export function ScheduleRows({ entries }: { entries: ScheduleEntry[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const id = useId();
  const groups = new Map<string, ScheduleEntry[]>();
  entries.forEach((entry) => {
    const day = localDay(entry.at);
    const group = groups.get(day) ?? [];
    group.push(entry);
    groups.set(day, group);
  });
  return (
    <div className="schedule-groups">
      {[...groups].map(([day, races], groupIndex) => (
        <section
          className="schedule-group"
          key={day}
          aria-labelledby={`${id}-day-${groupIndex}`}
        >
          <h2 id={`${id}-day-${groupIndex}`}>{day}</h2>
          <ul className="schedule-race-list">
            {races.map((race) => {
              const open = expanded === race.raceId;
              const summaryId = `${id}-${race.raceId}-summary`;
              const detailsId = `${id}-${race.raceId}-details`;
              const official = race.status === "official";
              return (
                <li className="schedule-row" key={race.raceId}>
                  <button
                    type="button"
                    className="schedule-row__summary"
                    id={summaryId}
                    aria-expanded={open}
                    aria-controls={detailsId}
                    aria-label={`${trackTitle(race)}, ${race.distanceM} metres, ${race.at ? localDateTime(race.at) : "finish date unavailable"}, ${race.status.replaceAll("_", " ")}${race.order?.length ? `, gate order ${race.order.map((gate) => gate + 1).join(", ")}` : ""}`}
                    onClick={() => setExpanded(open ? null : race.raceId)}
                  >
                    <span className="schedule-row__time">
                      {race.at ? (
                        <time dateTime={race.at}>{localTime(race.at)}</time>
                      ) : (
                        "—"
                      )}
                      <small>{official ? "Finished" : "Scheduled"}</small>
                    </span>
                    <span className="schedule-row__league">
                      <strong>{trackTitle(race)}</strong>
                      <span>
                        {race.raceNumber ? `${race.raceNumber} · ` : ""}
                        {race.distanceM}m
                        {race.fieldSize ? ` · ${race.fieldSize} runners` : ""}
                      </span>
                      {race.stage ? <small>{venueLabel(race)}</small> : null}
                    </span>
                    <span className="schedule-row__state">
                      {official ? "Official" : race.status.replaceAll("_", " ")}
                    </span>
                    {race.order ? (
                      <span className="schedule-row__order">
                        <small>Gate order</small>
                        <span>
                          {race.order.map((gate) => (
                            <b className={`gate gate--${gate + 1}`} key={gate}>
                              {gate + 1}
                            </b>
                          ))}
                        </span>
                      </span>
                    ) : null}
                    <span className="schedule-row__expand">
                      <span>{open ? "Close" : "Details"}</span>
                      <ChevronDown size={16} aria-hidden="true" />
                    </span>
                  </button>
                  <div
                    id={detailsId}
                    role="region"
                    aria-labelledby={summaryId}
                    hidden={!open}
                  >
                    {open ? (
                      <RaceDetails
                        raceId={race.raceId}
                        official={official}
                        racecardHash={race.racecardHash}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
